import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { authErrorMessage, isUserNotConfirmed } from '../auth/cognito';
import { useAuth } from '../auth/AuthContext';
import {
  AuthLayout,
  FormAlert,
  describedBy,
  readAuthState,
  type AuthRouteState,
} from '../components/AuthLayout';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { signIn } = useAuth();

  const arrival = readAuthState(location.state);
  const [email, setEmail] = useState(arrival.email ?? '');
  const [password, setPassword] = useState('');
  const [notice, setNotice] = useState(arrival.notice);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const next = safeNext(searchParams.get('next') ?? arrival.from);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;

    setBusy(true);
    setError(null);
    setNotice(undefined);
    try {
      await signIn(email.trim(), password);
      navigate(next, { replace: true });
    } catch (caught: unknown) {
      // An unconfirmed account is a step the creator can still finish, not a
      // failure: send them to the code form instead of leaving them at a form
      // that will keep rejecting a password that is actually correct.
      if (isUserNotConfirmed(caught)) {
        const state: AuthRouteState = {
          email: email.trim(),
          notice: 'Confirm your email to finish signing in. Enter the code we sent you, or send a new one.',
        };
        navigate('/verify', { state });
        return;
      }
      setError(authErrorMessage(caught, 'signIn'));
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Pick up where you left off with your portfolio."
      footer={
        <>
          New here? <Link className="link" to="/signup">Create an account</Link>
        </>
      }
    >
      {/* A real form, so Enter submits and the browser enforces `required`
          before a round trip can fail on an empty field. */}
      <form className="stack" onSubmit={onSubmit} aria-busy={busy}>
        <fieldset className="fieldset" disabled={busy}>
          {notice === undefined ? null : <FormAlert kind="success">{notice}</FormAlert>}
          {error === null ? null : (
            <FormAlert kind="error" id="login-error">
              {error}
            </FormAlert>
          )}

          <div className="field">
            <label className="label" htmlFor="login-email">
              Email
            </label>
            <input
              id="login-email"
              className="input"
              type="email"
              name="email"
              autoComplete="email"
              autoCapitalize="none"
              spellCheck={false}
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              aria-invalid={error === null ? undefined : true}
              aria-describedby={describedBy(error !== null && 'login-error')}
            />
          </div>

          <div className="field">
            <div className="row row--between">
              <label className="label" htmlFor="login-password">
                Password
              </label>
              <Link
                className="link text-sm"
                to="/forgot-password"
                state={{ email: email.trim() } satisfies AuthRouteState}
              >
                Forgot your password?
              </Link>
            </div>
            <input
              id="login-password"
              className="input"
              type="password"
              name="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              aria-invalid={error === null ? undefined : true}
              aria-describedby={describedBy(error !== null && 'login-error')}
            />
          </div>

          <button type="submit" className="btn btn--primary btn--block">
            {busy ? <span className="spinner spinner--sm" aria-hidden="true" /> : null}
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </fieldset>
      </form>
    </AuthLayout>
  );
}

/**
 * Only same-site paths are followed after sign-in. `next` arrives in the query
 * string, where anything can put it, so an absolute, protocol-relative or
 * backslash-prefixed value would turn this form into an open redirect that
 * lands a freshly authenticated creator on someone else's page.
 */
function safeNext(candidate: string | null | undefined): string {
  if (candidate === null || candidate === undefined || candidate === '') return '/app';
  if (!candidate.startsWith('/')) return '/app';
  if (candidate.startsWith('//') || candidate.startsWith('/\\')) return '/app';
  return candidate;
}
